// frontend/src/pages/Profile.jsx
// ✅ UPDATED: Price bundles with better value + Pay-as-you-go

import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { useAuth } from "../lib/useAuth";

import {
  apiGet,
  apiPost,
  normalizeListResponse,
  getImageUrl,
  toast,
  fromNow,
  shorten,
  fmtUSD
} from "../lib/api";
import StripeCheckout from "../components/StripeCheckout";

const STYLE_GUIDES_PATH = "/pricing";
const CREATOR_PROGRAM_PATH = "/creator-directory";

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
  shadow: "0 10px 24px rgba(17,24,39,.08)",
};

/* ------------------------------ Buy Credits ------------------------------- */
// Standard pay-as-you-go rate
const USD_PER_CREDIT = 0.17;

// ✅ PRICE BUNDLES with better value
const BUNDLES = [
  { price: 1, credits: 6, label: "$1" },
  { price: 5, credits: 30, label: "$5" },
  { price: 10, credits: 60, label: "$10" },
  { price: 25, credits: 150, label: "$25" },
];

function BuyCredits({ onAfterPurchase, currentCredits }) {
  const [qty, setQty] = React.useState(12);
  const [selectedBundle, setSelectedBundle] = React.useState(null);

  // ✅ Calculate pricing based on bundle or manual input
  const clamped = Math.max(1, Math.min(10000, Number.isFinite(+qty) ? +qty : 1));
  
  let usdAmount;
  let perCreditRate;
  
  if (selectedBundle) {
    // Bundle pricing (fixed)
    usdAmount = selectedBundle.price;
    perCreditRate = selectedBundle.price / selectedBundle.credits;
  } else {
    // Pay-as-you-go pricing (calculated from credits)
    usdAmount = clamped * USD_PER_CREDIT;
    perCreditRate = USD_PER_CREDIT;
  }

  const displayCredits = selectedBundle ? selectedBundle.credits : clamped;

  function handleBundleClick(bundle) {
    setSelectedBundle(bundle);
    setQty(bundle.credits);
  }

  function handleManualInput(value) {
    setQty(value);
    setSelectedBundle(null); // Clear bundle selection when manually typing
  }

  return (
    <div className="buy-inline">
      <div className="buy-inline-left">
        <label className="field">
          <span className="field__label">Select amount or choose a bundle</span>
          <input
            className="input"
            type="number"
            min={1}
            step={1}
            value={qty}
            onChange={(e) => handleManualInput(e.target.value)}
            placeholder="Enter credits"
          />
        </label>

        {/* ✅ UPDATED: Price bundles */}
        <div className="buy-pills">
          {BUNDLES.map((bundle) => (
            <button 
              key={bundle.price} 
              className={`pill ${selectedBundle?.price === bundle.price ? 'pill-active' : ''}`}
              onClick={() => handleBundleClick(bundle)}
              title={`${bundle.credits} credits`}
            >
              {bundle.label}
              <span className="pill-credits">{bundle.credits} credits</span>
            </button>
          ))}
        </div>
        
        {selectedBundle && (
          <div className="bundle-notice">
             Bundle selected: <strong>{selectedBundle.credits} credits</strong> for <strong>${selectedBundle.price}</strong>
          </div>
        )}
      </div>

      <div className="buy-inline-right">
        <div className="buy-summary-compact">
          <div className="row">
            <span>Selected Credits</span>
            <strong>{displayCredits}</strong>
          </div>
          <div className="row">
            <span>Total (USD)</span>
            <strong>${usdAmount.toFixed(2)}</strong>
          </div>
          <div className="row price-rate">
            <span>Per Credit</span>
            <span className={`price-badge ${selectedBundle ? 'price-badge-bundle' : ''}`}>
              ${perCreditRate.toFixed(3)}
            </span>
          </div>
          {selectedBundle && perCreditRate < USD_PER_CREDIT && (
            <div className="savings-badge">
               Save ${((USD_PER_CREDIT - perCreditRate) * selectedBundle.credits).toFixed(2)}
            </div>
          )}
        </div>
        
        {/* ✅ Stripe Checkout */}
        <StripeCheckout
          credits={displayCredits}
          onSuccess={() => onAfterPurchase()}
          disabled={false}
        />
        
{/* ✅ ADD THIS - Fee disclosure */}
<div className="fee-notice">
  Payment processing fees may apply
</div>
      </div>
    </div>
  );
}

/* ------------------------------- Component -------------------------------- */
export default function Profile() {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [credits, setCredits] = React.useState(0);
  const [user, setUser] = React.useState({ googleId: "", email: "", name: "" });
  const [items, setItems] = React.useState([]);
  const [totalImagesCount, setTotalImagesCount] = React.useState(0);
  const [sharedToHomeCount, setSharedToHomeCount] = React.useState(0);
  const [stylesUsedCount, setStylesUsedCount] = React.useState(0);
  const [hasStyles, setHasStyles] = React.useState(false);

  const [receiptsLoading, setReceiptsLoading] = React.useState(true);
  const [receiptsErr, setReceiptsErr] = React.useState("");
  const [receipts, setReceipts] = React.useState([]);

  const [payOpen, setPayOpen] = React.useState(() => {
    try { return localStorage.getItem("pf_pay_open") !== "0"; } catch { return true; }
  });
  
  const [statsOpen, setStatsOpen] = React.useState(() => {
    try { return localStorage.getItem("pf_stats_open") === "1"; } catch { return false; }
  });
  
  function togglePay() {
    setPayOpen((v) => {
      const next = !v;
      try { localStorage.setItem("pf_pay_open", next ? "1" : "0"); } catch {}
      return next;
    });
  }

  function toggleStats() {
    setStatsOpen((v) => {
      const next = !v;
      try { localStorage.setItem("pf_stats_open", next ? "1" : "0"); } catch {}
      return next;
    });
  }

  const refreshAllData = React.useCallback(async () => {
    try {
      const [creditsData, receiptsData] = await Promise.all([
        apiGet("/api/credits"),
        apiGet("/api/credits/receipts"),
      ]);

      setCredits(creditsData.credits ?? 0);
      if (Array.isArray(receiptsData)) {
        setReceipts(normalizeReceipts(receiptsData));
      }
    } catch (e) {
      console.warn("[Profile] Refresh failed:", e);
    }
  }, []);

  // Check if user has styles
React.useEffect(() => {
  async function checkStyles() {
    try {
      const res = await apiGet('/api/creator/styles');
      setHasStyles(res.items?.length > 0);
    } catch (e) {
      console.error("Failed to check styles:", e);
      setHasStyles(false);
    }
  }
  checkStyles();
}, []);

  React.useEffect(() => {
    let alive = true;
    
    (async () => {
      try {
        setLoading(true);

        const [userInfo, creditsInfo] = await Promise.all([
          apiGet("/api/users/me"),
          apiGet("/api/users/me/credits")
        ]);

        if (alive) {
          setCredits(creditsInfo.credits ?? 0);
          setUser({ 
            googleId: userInfo.googleId || "", 
            email: userInfo.email || "", 
            name: userInfo.name || userInfo.email || ""
          });
          console.log("[Profile] User loaded:", userInfo);
        }

        const [gj, statsData] = await Promise.all([
          apiGet("/api/generations"),
          apiGet("/api/generations/stats")
        ]);
        
        const { items: generations } = normalizeListResponse(gj);
        
        if (alive) { 
          setItems(generations);
          setTotalImagesCount(statsData.totalCount || 0);
          setSharedToHomeCount(statsData.sharedToHomeCount || 0);
          setStylesUsedCount(statsData.stylesUsedCount || 0);
        }
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load profile");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    (async () => {
      try {
        setReceiptsLoading(true);
        setReceiptsErr("");
        const j = await apiGet("/api/credits/receipts");
        if (alive) setReceipts(normalizeReceipts(j));
      } catch (e) {
        if (alive) setReceiptsErr(e.message || "Failed to load payments");
      } finally {
        if (alive) setReceiptsLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  const total = totalImagesCount;
  const stylesUsed = stylesUsedCount;
  const sharedToHome = sharedToHomeCount;
  const recent = items.slice(0, 12);

  function copyUserId() {
    if (!user.googleId) return;
    navigator.clipboard.writeText(user.googleId)
      .then(() => toast("User ID copied"))
      .catch(() => toast("Couldn't copy"));
  }

  function copyTx(tx) {
    if (!tx) return;
    navigator.clipboard.writeText(String(tx))
      .then(() => toast("Transaction hash copied"))
      .catch(() => toast("Couldn't copy"));
  }

  function exportCSV() {
    if (!receipts.length) return toast("Nothing to export");
    const header = ["date","credits","amount","status","id"].join(",");
    const rows = receipts.map((r, i) =>
      [
        new Date(r.createdAt).toISOString(),
        r.credits ?? "",
        r.amount ?? "",
        r.status ?? "",
        r.id ?? `rcpt-${i}`,
      ].map(v => (String(v).includes(",") ? `"${String(v).replace(/"/g,'""')}"` : String(v))).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "artibytes-receipts.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function refreshCredits() {
    try {
      const j = await apiGet("/api/credits");
      setCredits(j.credits ?? credits);
    } catch (e) {
      console.error("[Profile] Refresh credits failed:", e);
    }
  }

  function formatStyleName(style) {
    const normalized = String(style || "").toLowerCase().trim();
    if (!normalized || normalized === "default") {
      return "Standard";
    }
    return normalized
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return (
    <Screen>
      <StyleTag />

      {/* User Info */}
      <section className="pf-hero">
        <div className="pf-hero__left">
          <div className="pf-avatar" aria-hidden>
            {user.name || user.email?.replace("@", "")?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="pf-title">
            <h1 
              className="h1"
              style={{
                background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Hi, Creator
            </h1>
            <div className="pf-id">
              {user.googleId && (
                <button className="link" onClick={copyUserId} title="Copy user ID">
                  {shorten(user.googleId)}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Primary Actions */}
<section className="pf-actions-compact">
  {hasStyles && (
    <>
      <Link to={CREATOR_PROGRAM_PATH} className="btn">
        Royalties
      </Link>
      <Link to="/manage-styles" className="btn">
        Styles
      </Link>
    </>
  )}
  <button className="btn" onClick={toggleStats}>
    Your Activity
  </button>
</section>

      {/* Stats & Payment History */}
      {statsOpen && (
        <>
          <section className="pf-stats">
            <Stat label="Total images" value={total} />
            <Stat label="Custom styles used" value={stylesUsed} />
            <Stat label="Shared to Home" value={sharedToHome} />
          </section>

          <section className="pf-payment-history panel">
            <div className="section-head">
              <div>
                <h3 className="section-title">Payment History</h3>
                {receipts.length > 0 && (
                  <div className="section-subtle">
                    {receipts.length} transactions
                  </div>
                )}
              </div>

              <div className="panel-tools">
                <button className="btn btn-xs" onClick={exportCSV}>Export</button>
                <button className="btn btn-xs" onClick={refreshCredits}>Refresh</button>
                <button className="btn btn-xs" onClick={togglePay}>
                  {payOpen ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {payOpen && (
              <>
                {receiptsLoading ? (
                  <div className="pay-list">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="pay-item sk">
                        <div className="sk-line w40" />
                        <div className="sk-line w60" />
                      </div>
                    ))}
                  </div>
                ) : receiptsErr ? (
                  <div className="pf-empty">Couldn't load payments – {receiptsErr}</div>
                ) : !receipts.length ? (
                  <div className="pf-empty">No purchases yet. Buy credits to see receipts here.</div>
                ) : (
                  <div className="pay-list">
                    {receipts.map((r) => (
                      <div key={r.id} className="pay-item">
                        <div className="pay-top">
                          <div className="pay-title">
                            +{r.credits ?? "–"} credits
                            {typeof r.amount === "number" ? <span className="muted"> • ${r.amount.toFixed(2)}</span> : null}
                          </div>
                          <span className={`badge ${badgeCls(r.status)}`}>{labelStatus(r.status)}</span>
                        </div>
                        <div className="pay-sub">
                          <span className="muted">{fromNow(r.createdAt)}</span>
                          {r.id ? (
                            <>
                              <span className="dot">•</span>
                              <button className="link" onClick={() => copyTx(r.id)}>
                                {shorten(r.id)}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      {/* Credits Section */}
      <section className="pf-financial panel">
        <div className="panel-head">
          <div className="credits-balance-badge">
            <span className="credits-label">Available credits</span>
            <strong className="credits-number">{credits}</strong>
          </div>

          <div className="credits-explainer">
            <div className="credits-explainer-title">How credits are used</div>
            <ul>
              <li>
                Generate an image <span>→</span> <strong>1 credit</strong>
              </li>
              <li>
                Edit or refine a prompt <span>→</span> <strong>1 credit</strong>
              </li>
            </ul>
          </div>
        </div>

        <div className="financial-section">
          <BuyCredits
            currentCredits={credits}
            onAfterPurchase={(newCredits) => {
              setCredits(newCredits);
              refreshAllData();
            }}
          />
        </div>
      </section>

      {/* Recent Activity */}
      <section className="pf-recent">
        <header>
          <h2 className="h2">Your latest generated images</h2>
          <Link to="/gallery" className="link">View all →</Link>
        </header>

        {loading ? (
          <div className="ribbon">
            {Array.from({ length: 10 }).map((_, i) => <div key={i} className="sk-tile" />)}
          </div>
        ) : recent.length === 0 ? (
          <div className="pf-empty">Nothing here yet – head to Create and make something!</div>
        ) : (
          <div className="ribbon">
            {recent.map((it) => {
              const src = getImageUrl(it);
              return (
                <figure key={it.id} className="tile" title={it.prompt || ""}>
                  <img src={src} alt={it.prompt || "generation"} loading="lazy" />
                  <span className="chip">{formatStyleName(it.style)}</span>
                </figure>
              );
            })}
          </div>
        )}
      </section>

      {err && <div className="pf-error">{err}</div>}
    </Screen>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

function normalizeReceipts(arr) {
  return arr.map((r, i) => ({
    id: r.id || r._id || `rcpt-${i}`,
    credits: r.credits ?? r.amount ?? r.qty ?? null,
    amount: typeof r.amount === "number" ? r.amount : null,
    createdAt: r.createdAt ?? r.time ?? r.date ?? Date.now(),
    status: (r.status || "completed").toLowerCase(),
  }));
}

function badgeCls(status = "") {
  const s = String(status).toLowerCase();
  if (s === "confirmed" || s === "paid" || s === "complete" || s === "completed") return "ok";
  if (s === "pending" || s === "processing") return "warn";
  if (s === "failed" || s === "reversed" || s === "refunded") return "err";
  return "muted";
}

function labelStatus(status = "") {
  const s = String(status).toLowerCase();
  if (s === "ok" || s === "confirmed") return "Confirmed";
  if (s === "paid") return "Paid";
  if (s === "complete" || s === "completed") return "Complete";
  if (s === "pending" || s === "processing") return "Pending";
  if (s === "failed") return "Failed";
  if (s === "reversed") return "Reversed";
  if (s === "refunded") return "Refunded";
  return s || "–";
}

function StyleTag() {
  return (
    <style>{`
.pf-hero{
  display:flex; align-items:center; justify-content:flex-start;
  background: linear-gradient(180deg, ${brand.purpleTint}, #fff);
  border:1px solid ${brand.purpleBorder};
  border-radius:16px; padding:16px 18px; margin-bottom:20px;
}
.pf-hero__left{ display:flex; gap:12px; align-items:center; }
.pf-avatar{
  width:52px; height:52px; border-radius:50%;
  display:grid; place-items:center;
  background:${brand.purple}; color:#fff; font-weight:800;
  box-shadow:${brand.shadow};
}
.pf-title h1{ margin:0 0 4px 0; font-size:32px; }
.pf-id{ color:#6B7280; display:flex; align-items:center; gap:8px; }
@media (max-width: 720px){
  .pf-title h1{ font-size:24px; }
}
.link{ color:${brand.purple}; font-weight:700; background:none; border:none; padding:0; cursor:pointer; text-decoration: none; }
.link:hover{ text-decoration: underline; }

.pf-actions-compact{
  display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px;
}
.pf-actions-compact .btn{
  flex:1; min-width:0;
}
@media (max-width: 640px){
  .pf-actions-compact .btn{
    font-size:14px; padding:0 12px;
  }
}

.credits-explainer{
  margin-top:8px;
  font-size:13px;
  color:#6B7280;
}

.credits-explainer-title{
  font-weight:800;
  color:#6B7280;
  margin-bottom:4px;
}

.credits-explainer ul{
  list-style:none;
  padding:0;
  margin:0;
  display:grid;
  gap:2px;
}

.credits-explainer li{
  display:flex;
  align-items:center;
  gap:6px;
}

.credits-explainer strong{
  color:#6B7280;
  font-weight:800;
}

.credits-explainer span{
  opacity:0.6;
}

.credits-balance-badge{
  display:inline-flex;
  align-items:center;
  gap:10px;
  height:40px;
  padding:6px 12px;
  margin-bottom:12px;
  background:#fff;
  border:1px solid ${brand.purpleBorder};
  color:${brand.neutralText};
  border-radius:999px;
  box-shadow:0 2px 6px rgba(109,94,249,0.1);
  user-select:none;
}

.credits-label{
  font-size:14px;
  font-weight:700;
  color:#6B7280;
}

.credits-number{
  background:${brand.purple};
  color:#fff;
  font-size:16px;
  font-weight:900;
  border-radius:999px;
  padding:4px 10px;
  min-width:28px;
  text-align:center;
}

.btn{ 
  height:42px; padding:0 16px; border-radius:12px; 
  border:1px solid ${brand.purpleBorder}; background:${brand.purpleTint}; 
  color:${brand.neutralText}; font-weight:700; cursor:pointer; 
  transition: all 0.2s; text-decoration:none; 
  display:inline-flex; align-items:center; justify-content:center; 
}
.btn:hover{ background: #E9DEFF; border-color: ${brand.purple}; }
.btn-primary{ background:${brand.purple}; color:#fff; border-color:${brand.purple}; }
.btn-primary:hover{ background:${brand.purpleDark}; }
.btn:disabled{ opacity:.6; cursor:not-allowed; }
.btn.btn-xs{ height:30px; padding:0 10px; border-radius:10px; font-weight:800; }

.pf-stats{
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr));
  gap:12px; margin-bottom:20px;
}
@media (max-width: 720px){ 
  .pf-stats{ gap:8px; }
  .stat{ padding:10px 8px; }
  .stat__value{ font-size:20px; }
  .stat__label{ font-size:11px; }
}
.stat{
  background:#fff; border:1px solid ${brand.neutralBorder}; border-radius:14px;
  padding:14px; text-align:center; box-shadow:${brand.shadow};
}
.stat__value{ font-size:28px; font-weight:800; color:${brand.neutralText}; }
.stat__label{ color:${brand.neutralText}; opacity:.7; font-size:14px; margin-top:4px; }

.pf-financial{ margin-bottom:20px; }
.financial-section{ padding:16px 0; }
.financial-section:not(:last-child){ border-bottom:1px solid ${brand.neutralBorder}; }
.section-title{ font-size:18px; font-weight:800; margin:0 0 12px 0; color:${brand.neutralText}; }
.section-head{
  display:flex; align-items:center; justify-content:space-between; margin-bottom:12px;
}
@media (max-width: 640px){
  .section-head{ flex-direction: column; align-items: flex-start; gap: 10px; }
  .panel-tools{ width: 100%; }
  .panel-tools .btn{ flex: 1; }
}

.section-subtle{
  margin-top:4px;
  font-size:13px;
  font-weight:600;
  color:#6B7280;
}

.panel{
  background: linear-gradient(180deg, rgba(239,238,255,0.7) 0%, rgba(255,255,255,0.9) 60%); 
  border:1px solid ${brand.neutralBorder}; border-radius:16px; padding:18px;
  box-shadow:${brand.shadow};
}
.panel-head{ margin-bottom:12px; }
.panel-tools{ display:flex; gap:8px; }

/* ✅ Buy Credits with Bundles */
.buy-inline{
  display:grid; gap:16px; grid-template-columns: 1fr;
  background:#fff; border:1px solid ${brand.neutralBorder}; 
  border-radius:12px; padding:16px;
}
@media (min-width: 720px){
  .buy-inline{ grid-template-columns: 1.2fr 0.8fr; }
}
.buy-inline-left{ display:grid; gap:12px; }
.buy-inline-right{ display:grid; gap:8px; align-content:start; }
.field{ display:grid; gap:6px; }
.field__label{ 
  font-weight:800; 
  font-size:14px;
  color: ${brand.purple};
}
.input{
  width:100%; height:42px; border-radius:12px; 
  border:1px solid ${brand.neutralBorder};
  padding:8px 12px; background:#fff; font-size:16px;
  transition: all 0.2s;
}
.input:focus{
  outline: none;
  border-color: ${brand.purple};
  box-shadow: 0 0 0 3px ${brand.purpleTint};
}

/* ✅ Bundle Pills */
.buy-pills{ 
  display:flex; gap:8px; flex-wrap:wrap; 
}
.pill{
  position: relative;
  height:42px; 
  padding:0 16px; 
  border-radius:12px; 
  border:2px solid ${brand.neutralBorder};
  background:#fff; 
  font-weight:800; 
  font-size:15px;
  cursor:pointer; 
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.pill:hover{ 
  border-color:${brand.purple}; 
  background:${brand.purpleTint}; 
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(109, 94, 249, 0.2);
}

.pill-active{
  border-color:${brand.purple};
  background:${brand.purple};
  color:#fff;
}

.pill-active:hover{
  background:${brand.purpleDark};
  border-color:${brand.purpleDark};
}

/* ✅ Show credits on hover */
.pill-credits{
  font-size:10px;
  font-weight:600;
  opacity:0;
  position:absolute;
  bottom:-18px;
  left:50%;
  transform:translateX(-50%);
  white-space:nowrap;
  background:rgba(0,0,0,0.8);
  color:#fff;
  padding:2px 6px;
  border-radius:4px;
  pointer-events:none;
  transition: opacity 0.2s;
}

.pill:hover .pill-credits{
  opacity:1;
}

.pill-active .pill-credits{
  opacity:0;
}

/* ✅ Bundle selection notice */
.bundle-notice{
  padding:10px 12px;
  background:linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  border:1px solid #F59E0B;
  border-radius:8px;
  font-size:13px;
  color:#92400E;
  font-weight:600;
}

.buy-summary-compact{
  border:1px solid ${brand.neutralBorder}; 
  border-radius:10px; padding:12px;
  display:grid; gap:8px; background:#FAFAFA;
}
.buy-summary-compact .row{ 
  display:flex; align-items:center; justify-content:space-between; 
  font-size:14px; 
}
.buy-summary-compact .row.price-rate{
  padding-top:8px;
  border-top:1px solid ${brand.neutralBorder};
  margin-top:4px;
}
.price-badge{
  background:${brand.purpleTint};
  color:${brand.purple};
  padding:4px 8px;
  border-radius:6px;
  font-size:12px;
  font-weight:800;
  border:1px solid ${brand.purpleBorder};
}

/* ✅ Bundle badge - better value */
.price-badge-bundle{
  background:linear-gradient(135deg, #10B981 0%, #059669 100%);
  color:#fff;
  border:1px solid #059669;
}

/* ✅ Savings indicator */
.savings-badge{
  padding:8px;
  background:linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
  border:1px solid #6EE7B7;
  border-radius:8px;
  text-align:center;
  font-size:12px;
  font-weight:800;
  color:#065F46;
}

.buy-btn{ width:100%; }
.hint{ color:#6B7280; font-size:12px; }
.muted{ color:#6B7280; }

.fee-notice{
  text-align:center;
  font-size:11px;
  color:#9CA3AF;
  margin-top:8px;
  font-weight:600;
}

.pay-list{ display:grid; gap:10px; margin-top:12px; }
.pay-item{
  border:1px solid ${brand.neutralBorder}; border-radius:12px; padding:12px;
  background:#fff;
}
.pay-top{ display:flex; align-items:center; justify-content:space-between; gap:8px; }
.pay-title{ font-weight:800; color:${brand.neutralText}; }
.pay-sub{ display:flex; align-items:center; gap:8px; margin-top:6px; font-size:13px; }
.dot{ color:#9CA3AF; }
.badge{
  border-radius:999px; padding:6px 10px; font-size:12px; font-weight:900;
  border:1px solid #ECEEF2; background:#fff; color:#374151;
}
.badge.ok{ background:#E8F7F0; color:#096645; border-color:#CDECDD; }
.badge.warn{ background:#FFF7E8; color:#8a5a00; border-color:#F5E6C3; }
.badge.err{ background:#FFE9E9; color:#8a1f1f; border-color:#F2C2C2; }

.pf-recent{ margin-bottom:20px; }
.pf-recent header{ display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.ribbon{
  display:flex; gap:12px; overflow:auto; padding-bottom:8px;
  -webkit-overflow-scrolling: touch;
}
.tile{
  position:relative; width:140px; min-width:140px; border-radius:14px; overflow:hidden;
  border:1px solid ${brand.neutralBorder}; background:#fff; box-shadow:${brand.shadow};
}
.tile img{ width:100%; aspect-ratio:1/1; object-fit:cover; display:block; }
.tile .chip{
  position:absolute; left:8px; bottom:8px; padding:4px 8px; font-size:11px; font-weight:800;
  background:${brand.purple}; color:#fff; border-radius:999px; box-shadow:${brand.shadow};
}
.sk-tile{
  width:140px; min-width:140px; height:140px; border-radius:14px;
  background: linear-gradient(90deg,#f5f5f7,#ececf2,#f5f5f7); animation: sk 1.2s infinite;
}
.sk-line{ height:12px; background:#EEE; border-radius:8px; margin-bottom:6px; }
.sk-line.w40{ width:40%; }
.sk-line.w60{ width:60%; }
.sk{ opacity:0.6; }
.pf-empty{ padding:12px; color:#6B7280; border:1px dashed ${brand.neutralBorder}; border-radius:10px; text-align:center; }
.pf-error{
  margin-top:14px; color:#B91C1C; background:#FFF7F7; border:1px solid #F2C2C2;
  border-radius:12px; padding:12px;
}
@keyframes sk { 0%{background-position:-200px 0} 100%{background-position:calc(200px + 100%) 0} }
    `}</style>
  );
}